import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { ShieldCheck, Lock, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Authenticate against backend — the response includes is_admin which is the single source of truth
      const resp = await axios.post(`${API_URL}/auth/login`, {
        email: email.trim().toLowerCase(),
        password
      });

      const loggedUser = resp.data?.user;
      if (!loggedUser?.is_admin) {
        toast.error('Acesso negado — esta conta não tem privilégios administrativos.');
        return;
      }

      await login(email.trim().toLowerCase(), password);
      toast.success('Acesso autorizado');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 ${
        isDark ? 'bg-slate-950' : 'bg-slate-100'
      }`}
      data-testid="admin-login-page"
    >
      <div
        className={`max-w-md w-full rounded-2xl border p-8 shadow-2xl ${
          isDark ? 'bg-slate-900/90 border-slate-800' : 'bg-white border-slate-200'
        }`}
      >
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center mb-4">
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <h1 className={`text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Painel Administrativo
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Acesso restrito — Akali AI Consultant
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="admin-email" className={isDark ? 'text-slate-300' : 'text-slate-700'}>
              E-mail administrativo
            </Label>
            <div className="relative">
              <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              <Input
                id="admin-email"
                type="email"
                placeholder="admin@akalipilates.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`pl-10 ${isDark ? 'bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500' : ''}`}
                required
                autoComplete="off"
                data-testid="admin-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-password" className={isDark ? 'text-slate-300' : 'text-slate-700'}>
              Senha
            </Label>
            <PasswordInput
              id="admin-password"
              leftIcon={Lock}
              iconColor={isDark ? 'text-slate-500' : 'text-slate-400'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${isDark ? 'bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500' : ''}`}
              required
              autoComplete="off"
              data-testid="admin-password-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-amber-500 to-red-500 hover:opacity-90 text-white"
            disabled={loading}
            data-testid="admin-login-submit"
          >
            {loading ? 'Verificando...' : 'Acessar painel'}
          </Button>
        </form>

        <div className="text-center mt-4">
          <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="admin-forgot-password-link">
            Esqueci minha senha
          </Link>
        </div>

        <p className={`text-xs mt-6 text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          Tentativas de acesso são registradas. Apenas administradores autorizados.
        </p>
      </div>
    </div>
  );
}
